import React, { useEffect, useState } from "react";
import { api, formatErr, API_BASE } from "@/lib/api";
import { PageHeader } from "@/components/AppShell";
import { fmtEur } from "@/lib/format";
import { Plus, Trash2, Pencil, Sun, Battery, Zap, X, Tags, Plug, ShieldCheck, Cpu, FileText, Paperclip, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";
import PriceLists from "@/pages/PriceLists";

// Tab del catalogo. Gli "extra" (ricarica / backup / ottimizzatori) condividono
// l'endpoint /products/extras filtrato per categoria.
const TABS = [
  { key: "panels", label: "Pannelli", icon: Sun, ep: "/products/panels" },
  { key: "batteries", label: "Batterie", icon: Battery, ep: "/products/batteries" },
  { key: "inverters", label: "Inverter", icon: Zap, ep: "/products/inverters" },
  { key: "charging_station", label: "Ricarica", icon: Plug, ep: "/products/extras", category: "charging_station" },
  { key: "backup", label: "Backup", icon: ShieldCheck, ep: "/products/extras", category: "backup" },
  { key: "optimizer", label: "Ottimizzatori", icon: Cpu, ep: "/products/extras", category: "optimizer" },
  { key: "pricelists", label: "Listini", icon: Tags, ep: null },
];

const PHASE_FIELD = { k: "phases", l: "Fasi", sel: [["monofase", "Monofase"], ["trifase", "Trifase"]] };
const PHASE_FIELD_ANY = { k: "phases", l: "Fasi", sel: [["any", "Indifferente"], ["monofase", "Monofase"], ["trifase", "Trifase"]] };
const PRICES = [
  { k: "purchase_price", l: "Costo €", t: "number" },
  { k: "sale_price", l: "Vendita €", t: "number" },
  { k: "min_sale_price", l: "Prezzo minimo €", t: "number" },
];
const EXTRA_FIELDS = [
  { k: "brand", l: "Marca" },
  { k: "model", l: "Modello" },
  PHASE_FIELD_ANY,
  { k: "power_kw", l: "Potenza (kW)", t: "number" },
  ...PRICES,
];

const FIELDS = {
  panels: [
    { k: "brand", l: "Marca" },
    { k: "model", l: "Modello" },
    { k: "technology", l: "Tecnologia" },
    { k: "power_wp", l: "Potenza (Wp)", t: "number" },
    { k: "width_mm", l: "Larghezza (mm)", t: "number" },
    { k: "height_mm", l: "Altezza (mm)", t: "number" },
    { k: "efficiency", l: "Efficienza (%)", t: "number" },
    ...PRICES,
  ],
  batteries: [
    { k: "brand", l: "Marca" },
    { k: "model", l: "Modello" },
    { k: "capacity_kwh", l: "Capacità (kWh)", t: "number" },
    { k: "usable_capacity_kwh", l: "Capacità utile", t: "number" },
    { k: "charge_power_kw", l: "Potenza (kW)", t: "number" },
    ...PRICES,
  ],
  inverters: [
    { k: "brand", l: "Marca" },
    { k: "model", l: "Modello" },
    { k: "ac_power_kw", l: "Potenza AC (kW)", t: "number" },
    PHASE_FIELD,
    { k: "mppt_count", l: "MPPT", t: "number" },
    ...PRICES,
  ],
  charging_station: EXTRA_FIELDS,
  backup: EXTRA_FIELDS,
  optimizer: EXTRA_FIELDS,
};

export default function Catalog() {
  const [tab, setTab] = useState("panels");
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);   // null = nuovo prodotto, altrimenti id in modifica
  const [aiBusy, setAiBusy] = useState(false);
  const [pendingDatasheet, setPendingDatasheet] = useState(null); // PDF caricato da allegare al salvataggio
  const dsRef = React.useRef();

  const tabDef = TABS.find((t) => t.key === tab);
  const reload = () => {
    if (!tabDef.ep) return;
    const url = tabDef.category ? `${tabDef.ep}?category=${tabDef.category}` : tabDef.ep;
    api.get(url).then((r) => setList(r.data));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tabDef.ep) reload(); setShowForm(false); setEditId(null); }, [tab]);

  const openCreate = () => { setForm({}); setEditId(null); setPendingDatasheet(null); setShowForm(true); };
  const openEdit = (p) => { setForm({ ...p }); setEditId(p.id); setPendingDatasheet(null); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditId(null); setPendingDatasheet(null); };

  const save = async () => {
    try {
      const payload = tabDef.category ? { ...form, category: tabDef.category } : form;
      let prodId = editId;
      if (editId) {
        await api.patch(`${tabDef.ep}/${editId}`, payload);
      } else {
        const { data } = await api.post(tabDef.ep, payload);
        prodId = data.id;
      }
      let msg = editId ? "Prodotto aggiornato" : "Prodotto creato";
      // Allega automaticamente la scheda tecnica caricata (se presente)
      if (pendingDatasheet && prodId) {
        const ptype = tabDef.category ? "extras" : tab;
        try {
          await api.post(`/products/${ptype}/${prodId}/datasheet`, {
            data_base64: pendingDatasheet.dataUrl, name: pendingDatasheet.name, mime: pendingDatasheet.mime,
          });
          msg += " · scheda allegata";
        } catch (e) {
          msg += " (scheda non allegata: PDF troppo grande, allegala dalla card)";
        }
      }
      toast.success(msg);
      setShowForm(false); setForm({}); setEditId(null); setPendingDatasheet(null); reload();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`${tabDef.ep}/${id}`); reload();
  };

  // Estrazione automatica dei campi dalla scheda tecnica (PDF) via AI locale (Ollama).
  const extractFromDatasheet = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Carica la scheda tecnica in PDF"); return; }
    if (file.size > 6 * 1024 * 1024) { toast.error("PDF troppo grande: max 6MB"); return; }
    setAiBusy(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(fr.result);
        fr.onerror = () => rej(new Error("Lettura file fallita"));
        fr.readAsDataURL(file);
      });
      // Ricorda il PDF: verrà allegato al prodotto al salvataggio, anche se l'estrazione non trova campi.
      setPendingDatasheet({ dataUrl, name: file.name, mime: file.type });
      const kind = tabDef.category ? "extras" : tab;
      const { data } = await api.post("/ai/extract-datasheet", { file_base64: dataUrl, kind });
      const extracted = data?.data || {};
      const n = Object.keys(extracted).length;
      if (!n) { toast.message("Scheda caricata (verrà allegata al salvataggio), ma nessun campo riconosciuto: compila a mano"); return; }
      setForm((prev) => ({ ...prev, ...extracted }));
      toast.success(`${n} campi compilati — la scheda verrà allegata al salvataggio. Controlla e salva`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setAiBusy(false); }
  };

  const isExtra = Boolean(tabDef.category);

  if (tab === "pricelists") {
    return (
      <div data-testid="page-catalog">
        <PageHeader eyebrow="Catalogo" title="Prodotti & Listini" subtitle="Gestisci pannelli, batterie, inverter, accessori e listini" />
        <div className="px-8 py-6">
          <Tabbar tab={tab} setTab={setTab} />
          <PriceLists/>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-catalog">
      <PageHeader eyebrow="Catalogo" title="Prodotti" subtitle="Pannelli, batterie, inverter e accessori del tuo listino" actions={
        <button onClick={openCreate} className="px-4 py-2.5 bg-zinc-900 text-white font-semibold rounded-xl text-sm flex items-center gap-2" data-testid="add-product-btn">
          <Plus size={14} /> Aggiungi
        </button>
      }/>
      <div className="px-8 py-6">
        <Tabbar tab={tab} setTab={setTab} />

        {showForm && (
          <div className="bg-white border border-zinc-200/70 rounded-2xl p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <div className="font-semibold">{editId ? "Modifica" : "Nuovo"} · {tabDef.label}</div>
              <button onClick={closeForm} className="text-zinc-500"><X size={16}/></button>
            </div>
            <div className="mb-4 flex items-center gap-3 flex-wrap bg-amber-50/70 border border-amber-200/70 rounded-xl px-3 py-2.5">
              <Sparkles size={15} className="text-amber-600 shrink-0" />
              {pendingDatasheet ? (
                <span className="text-xs text-zinc-700 min-w-0 flex items-center gap-1.5">
                  <FileText size={13} className="text-amber-600 shrink-0" />
                  <span className="truncate">{pendingDatasheet.name}</span>
                  <span className="text-zinc-400">· verrà allegata al salvataggio</span>
                </span>
              ) : (
                <span className="text-xs text-zinc-600 min-w-0">Carica la <b>scheda tecnica (PDF)</b>: l'AI compila i campi e la <b>allega</b> al prodotto.</span>
              )}
              <input ref={dsRef} type="file" accept="application/pdf" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; extractFromDatasheet(f); }} data-testid="datasheet-ai-input" />
              <button type="button" onClick={() => dsRef.current.click()} disabled={aiBusy}
                className="ml-auto shrink-0 px-3 py-1.5 bg-zinc-900 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-wait" data-testid="datasheet-ai-btn">
                {aiBusy ? "Analisi in corso…" : <><Upload size={12}/> {pendingDatasheet ? "Cambia scheda" : "Carica scheda"}</>}
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {FIELDS[tab].map((f) => (
                <div key={f.k}>
                  <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{f.l}</label>
                  {f.sel ? (
                    <select value={form[f.k] ?? f.sel[0][0]}
                      onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                      className="w-full mt-1 bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 text-sm" data-testid={`product-field-${f.k}`}>
                      {f.sel.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  ) : (
                    <input type={f.t || "text"} value={form[f.k] ?? ""}
                      onChange={(e) => setForm({...form, [f.k]: f.t === "number" ? parseFloat(e.target.value) || 0 : e.target.value})}
                      className="w-full mt-1 bg-zinc-50 border border-zinc-200/70 rounded-lg px-3 py-2 text-sm" data-testid={`product-field-${f.k}`} />
                  )}
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-zinc-400">Il <span className="text-amber-600">prezzo minimo</span> è il limite sotto cui il preventivatore non può scendere.</p>
            <button onClick={save} className="mt-4 bg-zinc-900 text-white font-semibold rounded-xl px-5 py-2.5" data-testid="product-save-btn">{editId ? "Salva modifiche" : "Salva"}</button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.length === 0 && <div className="col-span-full text-center text-zinc-500 py-16">Nessun prodotto</div>}
          {list.map((p) => (
            <div key={p.id} className="bg-white border border-zinc-200/70 rounded-2xl p-5 hover:border-zinc-300 transition-colors" data-testid="product-card">
              <div className="flex items-start justify-between mb-3">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-600">{p.brand}</div>
                <div className="flex items-center gap-2">
                  <button onClick={() => openEdit(p)} className="text-zinc-500 hover:text-amber-600" title="Modifica" data-testid="edit-product-btn"><Pencil size={14}/></button>
                  <button onClick={() => del(p.id)} className="text-zinc-500 hover:text-rose-400" title="Elimina"><Trash2 size={14}/></button>
                </div>
              </div>
              <div className="font-semibold text-lg leading-tight">{p.model}</div>
              <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                {tab === "panels" && (
                  <>
                    <div className="text-zinc-500">Potenza</div><div className="text-right font-mono text-amber-600">{p.power_wp} Wp</div>
                    <div className="text-zinc-500">Tecnologia</div><div className="text-right">{p.technology || "—"}</div>
                    <div className="text-zinc-500">Efficienza</div><div className="text-right font-mono">{p.efficiency || "—"}%</div>
                    <div className="text-zinc-500">Dimensioni</div><div className="text-right font-mono text-[11px]">{p.width_mm}×{p.height_mm}mm</div>
                  </>
                )}
                {tab === "batteries" && (
                  <>
                    <div className="text-zinc-500">Capacità</div><div className="text-right font-mono text-amber-600">{p.capacity_kwh} kWh</div>
                    <div className="text-zinc-500">Utilizzabile</div><div className="text-right font-mono">{p.usable_capacity_kwh || "—"} kWh</div>
                    <div className="text-zinc-500">Potenza</div><div className="text-right font-mono">{p.charge_power_kw || "—"} kW</div>
                  </>
                )}
                {tab === "inverters" && (
                  <>
                    <div className="text-zinc-500">Potenza AC</div><div className="text-right font-mono text-amber-600">{p.ac_power_kw} kW</div>
                    <div className="text-zinc-500">Fasi</div><div className="text-right capitalize">{p.phases}</div>
                    <div className="text-zinc-500">MPPT</div><div className="text-right font-mono">{p.mppt_count}</div>
                  </>
                )}
                {isExtra && (
                  <>
                    {p.power_kw ? (<><div className="text-zinc-500">Potenza</div><div className="text-right font-mono text-amber-600">{p.power_kw} kW</div></>) : null}
                    <div className="text-zinc-500">Fasi</div><div className="text-right capitalize">{p.phases === "any" ? "Indifferente" : p.phases}</div>
                  </>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-zinc-200/70 flex justify-between text-sm">
                <div><div className="text-[10px] text-zinc-500 font-mono uppercase tracking-wide">Vendita</div><div className="font-mono text-emerald-600">{fmtEur(p.sale_price)}</div></div>
                {p.min_sale_price != null && (
                  <div className="text-center"><div className="text-[10px] text-zinc-500 font-mono uppercase tracking-wide">Minimo</div><div className="font-mono text-amber-600">{fmtEur(p.min_sale_price)}</div></div>
                )}
                <div className="text-right"><div className="text-[10px] text-zinc-500 font-mono uppercase tracking-wide">Margine</div><div className="font-mono text-zinc-900">{fmtEur((p.sale_price || 0) - (p.purchase_price || 0))}</div></div>
              </div>
              <DatasheetRow p={p} ptype={isExtra ? "extras" : tab} onChange={reload} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Riga "scheda tecnica" per ogni prodotto: allega / apri / rimuovi un PDF o immagine
// che verrà poi accodato al business plan. Il file è letto come data-URL base64.
function DatasheetRow({ p, ptype, onChange }) {
  const [busy, setBusy] = useState(false);
  const inputId = `ds-${p.id}`;

  const upload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.type.startsWith("image/")) {
      toast.error("Allega un PDF o un'immagine"); return;
    }
    if (file.size > 6 * 1024 * 1024) { toast.error("File troppo grande: max 6MB"); return; }
    setBusy(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(fr.result);
        fr.onerror = () => rej(new Error("Lettura file fallita"));
        fr.readAsDataURL(file);
      });
      await api.post(`/products/${ptype}/${p.id}/datasheet`, { data_base64: dataUrl, name: file.name, mime: file.type });
      toast.success("Scheda tecnica allegata");
      onChange();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const view = async () => {
    const token = localStorage.getItem("sf_token");
    const tab = window.open("", "_blank");
    try {
      const r = await fetch(`${API_BASE}/products/${ptype}/${p.id}/datasheet`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { if (tab) tab.close(); toast.error("Scheda non disponibile"); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      if (tab) tab.location = url; else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (tab) tab.close(); toast.error(formatErr(e)); }
  };

  const remove = async () => {
    if (!window.confirm("Rimuovere la scheda tecnica?")) return;
    setBusy(true);
    try { await api.delete(`/products/${ptype}/${p.id}/datasheet`); toast.success("Scheda rimossa"); onChange(); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-zinc-200/70">
      {p.datasheet_name ? (
        <div className="flex items-center gap-2">
          <button onClick={view} className="flex items-center gap-1.5 min-w-0 text-xs text-zinc-700 hover:text-amber-600" title="Apri scheda tecnica" data-testid="datasheet-view">
            <FileText size={13} className="shrink-0 text-amber-600" />
            <span className="truncate">{p.datasheet_name}</span>
          </button>
          <button onClick={remove} disabled={busy} className="ml-auto shrink-0 text-zinc-400 hover:text-rose-400" title="Rimuovi scheda" data-testid="datasheet-remove">
            <X size={13} />
          </button>
        </div>
      ) : (
        <label htmlFor={inputId} className={`flex items-center gap-1.5 text-xs cursor-pointer ${busy ? "text-zinc-400 cursor-wait" : "text-zinc-500 hover:text-zinc-900"}`} data-testid="datasheet-attach">
          <Paperclip size={13} /> {busy ? "Caricamento…" : "Allega scheda tecnica"}
        </label>
      )}
      <input id={inputId} type="file" accept="application/pdf,image/*" className="hidden" disabled={busy}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }} />
    </div>
  );
}

function Tabbar({ tab, setTab }) {
  return (
    <div className="inline-flex flex-wrap gap-1 bg-white border border-zinc-200/70 rounded-xl p-1 mb-6">
      {TABS.map((t) => {
        const Ic = t.icon;
        const active = tab === t.key;
        return (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3.5 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${active ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-900"}`}
            data-testid={`tab-${t.key}`}
          >
            <Ic size={14} /> {t.label}
          </button>
        );
      })}
    </div>
  );
}
